import React, {useContext, useMemo, useState} from 'react';
import {Button, Form, InputNumber, Select, Table, Input, Modal, Badge} from 'antd';
import {internalApi} from "../../../common/axios";
import {DIALOG_KEY} from "../../../shared/utils/enum";
import {buildUrl} from "../../../shared/utils/net";
import {CancelButton, DeleteButton, FixButton, SaveButton} from "../../../shared/components/buttons/CustomButtons";
import {useItemKrTextData} from "../../probability/useItemData";
import {useEventTable} from "../useEventTable";
import {AuthContext} from "../../../shared/contexts/authContext";
import IsLoading from "../../../shared/components/IsLoading";
import toast from "react-hot-toast";
import {useQueryClient} from "@tanstack/react-query";
import ItemRewardSlot from "../../../shared/components/rewards/ItemRewardSlot";

const {Option} = Select;

const DIFFICULTY_MAP = {1: '쉬움', 2: '보통', 3: '어려움'};
const GROUP_TYPE_MAP = {1: '이벤트', 2: '보상'};
const BOX_TYPE_MAP = {0: '-', 1: '일반', 2: '최종'};
const IS_NEW_MAP = {0: '없음', 1: '신규'};

const ShoppingRoadEventEditModal = ({record, handleClose, dialogRef, handleSetDialog, modalOptions}) => {
    const queryClient = useQueryClient();
    const {currentUser} = useContext(AuthContext);
    const {data: itemRaw, isLoading: isItemLoading} = useItemKrTextData();
    const itemData = itemRaw?.[currentUser?.target] || [];
    const {data: eventTable, isLoading: isEventTableLoading} = useEventTable();

    const tbl = modalOptions?.tbl || {};

    // tbl을 groupIdx 기준으로 그룹화
    const groupedTbl = useMemo(() => {
        const groups = {};
        Object.entries(tbl).forEach(([idx, entry]) => {
            const gIdx = entry.groupIdx;
            if (!groups[gIdx]) groups[gIdx] = [];
            groups[gIdx].push({...entry, _idx: Number(idx)});
        });
        return groups;
    }, [tbl]);
    const groupKeys = Object.keys(groupedTbl).sort((a, b) => Number(a) - Number(b));

    const allRows = modalOptions?.allRows || [];
    const initialGroupIdx = record?.groupIdx || (() => {
        if (!allRows.length) return 1;
        return Math.max(...allRows.map(r => r.groupIdx)) + 1;
    })();
    const initialItems = (record?.items || []).map(item => ({
        groupSeq: item.groupSeq || 0,
        difficulty: item.difficulty || 1,
        groupType: item.groupType || 1,
        eventIdx: item.eventIdx || 0,
        boxType: item.boxType || 0,
        isNew: item.isNew || 0,
        rewards: (() => {
            const r = item.rewards;
            if (!r) return [];
            if (Array.isArray(r)) return r;
            try {
                return JSON.parse(r);
            } catch {
                return [];
            }
        })(),
        memo: item.memo || '',
    }));

    const [groupIdx, setGroupIdx] = useState(initialGroupIdx);
    const [items, setItems] = useState(initialItems);
    const [memo, setMemo] = useState(initialItems[0]?.memo || '');
    const [selectedTblIdx, setSelectedTblIdx] = useState(null);

    const handleApplyTbl = () => {
        if (!selectedTblIdx && selectedTblIdx !== 0) return;
        const groupEntries = groupedTbl[selectedTblIdx];
        console.log(groupEntries);
        if (!groupEntries?.length) return;

        const newItems = groupEntries.map(entry => ({
            groupSeq: 0, // 뒤에서 재번호
            difficulty: entry.difficulty || 1,
            groupType: entry.groupType || 1,
            eventIdx: entry.eventIdx || 0,
            boxType: entry.boxType || 0,
            rewards: (entry.rewards || []).map(r => ({
                type: r.type || 0,
                id: r.id || 0,
                quantity: r.quantity || 0,
            })),
            memo: '',
        }));

        setGroupIdx(Number(selectedTblIdx));
        setItems(newItems.map((it, i) => ({...it, groupSeq: i + 1})));
    };

    const handleAddItem = () => {
        setItems(prev => [...prev, {
            groupSeq: prev.length + 1,
            difficulty: 1,
            groupType: 1,
            eventIdx: 0,
            boxType: 0,
            rewards: [],
            memo: '',
        }]);
    };

    const handleUpdateItem = (index, key, value) => {
        setItems(prev => prev.map((item, i) => i === index ? {...item, [key]: value} : item));
    };

    const handleMoveUp = (index) => {
        if (index === 0) return;
        setItems(prev => {
            const next = [...prev];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next.map((it, i) => ({...it, groupSeq: i + 1}));
        });
    };

    const handleMoveDown = (index) => {
        setItems(prev => {
            if (index >= prev.length - 1) return prev;
            const next = [...prev];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next.map((it, i) => ({...it, groupSeq: i + 1}));
        });
    };

    const handleRemoveItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index).map((it, i) => ({...it, groupSeq: i + 1})));
    };

    const doSave = () => {
        const saveData = items.map((item, idx) => ({
            groupIdx: groupIdx,
            groupSeq: idx + 1,
            difficulty: item.difficulty,
            groupType: item.groupType,
            eventIdx: item.groupType === 1 ? item.eventIdx : 0,
            boxType: item.groupType === 2 ? item.boxType : 0,
            isNew: item.isNew || 0,
            rewards: JSON.stringify(item.groupType === 2 ? item.rewards : []),
            memo: memo,
        }));

        return internalApi.post('events/shopping_road/group', {data: saveData}).then((res) => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['shoppingRoadGroup']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch((err) => {
            console.error(err);
            toast.error('저장 실패');
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (!groupIdx || groupIdx <= 0) {
            toast.error('그룹 번호는 0보다 커야 합니다.');
            return;
        }

        const isDuplicate = groupIdx !== record?.groupIdx && allRows.some(r => r.groupIdx === groupIdx);

        if (isDuplicate) {
            Modal.confirm({
                title: '중복 확인',
                content: '이미 존재한 번호입니다. 내용을 수정해서 저장하시겠습니까?',
                okText: '예',
                cancelText: '아니오',
                onOk: doSave,
            });
        } else {
            return doSave();
        }
    };

    const handleDelete = (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            return internalApi.delete(buildUrl('events/shopping_road/group', {groupIdx: groupIdx})).then((res) => {
                toast.success(res.data.message);
                queryClient.invalidateQueries({queryKey: ['shoppingRoadGroup']});
                handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
                handleClose(true);
            }).catch((err) => {
                toast.error('삭제 실패');
                console.error(err);
            });
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    };

    const handleFix = () => {
        internalApi.post('events/shopping_road/group_info/fix', {groupIdx: groupIdx}).then((res) => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['shoppingRoadGroup']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch((err) => {
            toast.error('고정 실패');
            console.error(err);
        });
    }

    const tableCols = [
        {
            title: '순서',
            key: 'groupSeq',
            width: 50,
            render: (_, __, index) => index + 1,
        },
        {
            title: '난이도',
            key: 'difficulty',
            width: 90,
            render: (_, item, index) => (
                <Select
                    size="small"
                    value={item.difficulty}
                    style={{width: '100%'}}
                    onChange={val => handleUpdateItem(index, 'difficulty', val)}
                >
                    {Object.entries(DIFFICULTY_MAP).map(([v, label]) => (
                        <Option key={v} value={Number(v)}>{label}</Option>
                    ))}
                </Select>
            ),
        },
        {
            title: '뱃지',
            key: 'badge',
            width: 100,
            render: (_, item, index) => (
                <Select
                    size="small"
                    value={item.isNew}
                    style={{width: '100%'}}
                    onChange={val => handleUpdateItem(index, 'isNew', val)}
                >
                    {Object.entries(IS_NEW_MAP).map(([v, label]) => (
                        <Option key={v} value={Number(v)}>{label}</Option>
                    ))}
                </Select>
            )
        },
        {
            title: '타입',
            key: 'groupType',
            width: 90,
            render: (_, item, index) => (
                <Select
                    size="small"
                    value={item.groupType}
                    style={{width: '100%'}}
                    onChange={val => handleUpdateItem(index, 'groupType', val)}
                >
                    {Object.entries(GROUP_TYPE_MAP).map(([v, label]) => (
                        <Option key={v} value={Number(v)}>{label}</Option>
                    ))}
                </Select>
            ),
        },
        {
            title: '이벤트 번호',
            key: 'eventIdx',
            width: 130,
            render: (_, item, index) => (
                <Select
                    size="small"
                    value={item.eventIdx || undefined}
                    style={{width: '100%'}}
                    disabled={item.groupType !== 1}
                    placeholder="이벤트 선택"
                    showSearch
                    filterOption={(input, opt) => {
                        const entry = eventTable?.[opt.value];
                        return (
                            String(opt.value).includes(input) ||
                            entry?.nameText?.toLowerCase().includes(input.toLowerCase())
                        );
                    }}
                    onChange={val => handleUpdateItem(index, 'eventIdx', val)}
                >
                    {eventTable && Object.values(eventTable).map(ev => (
                        <Option key={ev.index} value={ev.index}>
                            {ev.nameText}({ev.index})
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: '상자 종류',
            key: 'boxType',
            width: 90,
            render: (_, item, index) => (
                <Select
                    size="small"
                    value={item.boxType}
                    style={{width: '100%'}}
                    disabled={item.groupType !== 2}
                    onChange={val => handleUpdateItem(index, 'boxType', val)}
                >
                    {Object.entries(BOX_TYPE_MAP).map(([v, label]) => (
                        <Option key={v} value={Number(v)}>{label}</Option>
                    ))}
                </Select>
            ),
        },
        {
            title: '보상 1',
            key: 'reward0',
            width: 140,
            render: (_, item, index) => {
                if (item.groupType !== 2) return '-';
                const slot = item.rewards?.[0];
                if (!slot) return (
                    <Button size="small" onClick={() => {
                        const newRewards = [...(item.rewards || [])];
                        newRewards[0] = {id: 0, type: 0, quantity: 0};
                        handleUpdateItem(index, 'rewards', newRewards);
                    }}>+ 추가</Button>
                );
                return (
                    <div>
                        <ItemRewardSlot
                            slot={slot}
                            itemData={itemData}
                            onChange={val => {
                                const newRewards = [...(item.rewards || [])];
                                newRewards[0] = val;
                                handleUpdateItem(index, 'rewards', newRewards);
                            }}
                        />
                        <Button size="small" danger style={{width: '100%', marginTop: 2}} onClick={() => {
                            const newRewards = [...(item.rewards || [])];
                            newRewards.splice(0, 1);
                            handleUpdateItem(index, 'rewards', newRewards);
                        }}>삭제</Button>
                    </div>
                );
            },
        },
        {
            title: '보상 2',
            key: 'reward1',
            width: 140,
            render: (_, item, index) => {
                if (item.groupType !== 2) return '-';
                const slot = item.rewards?.[1];
                if (!slot) return (
                    <Button size="small" onClick={() => {
                        const newRewards = [...(item.rewards || [])];
                        newRewards[1] = {id: 0, type: 0, quantity: 0};
                        handleUpdateItem(index, 'rewards', newRewards);
                    }}>+ 추가</Button>
                );
                return (
                    <div>
                        <ItemRewardSlot
                            slot={slot}
                            itemData={itemData}
                            onChange={val => {
                                const newRewards = [...(item.rewards || [])];
                                newRewards[1] = val;
                                handleUpdateItem(index, 'rewards', newRewards);
                            }}
                        />
                        <Button size="small" danger style={{width: '100%', marginTop: 2}} onClick={() => {
                            const newRewards = [...(item.rewards || [])];
                            newRewards.splice(1, 1);
                            handleUpdateItem(index, 'rewards', newRewards);
                        }}>삭제</Button>
                    </div>
                );
            },
        },
        {
            title: '보상 3',
            key: 'reward2',
            width: 140,
            render: (_, item, index) => {
                if (item.groupType !== 2) return '-';
                const slot = item.rewards?.[2];
                if (!slot) return (
                    <Button size="small" onClick={() => {
                        const newRewards = [...(item.rewards || [])];
                        newRewards[2] = {id: 0, type: 0, quantity: 0};
                        handleUpdateItem(index, 'rewards', newRewards);
                    }}>+ 추가</Button>
                );
                return (
                    <div>
                        <ItemRewardSlot
                            slot={slot}
                            itemData={itemData}
                            onChange={val => {
                                const newRewards = [...(item.rewards || [])];
                                newRewards[2] = val;
                                handleUpdateItem(index, 'rewards', newRewards);
                            }}
                        />
                        <Button size="small" danger style={{width: '100%', marginTop: 2}} onClick={() => {
                            const newRewards = [...(item.rewards || [])];
                            newRewards.splice(2, 1);
                            handleUpdateItem(index, 'rewards', newRewards);
                        }}>삭제</Button>
                    </div>
                );
            },
        },
        {
            title: '조작',
            key: 'actions',
            width: 100,
            render: (_, __, index) => (
                <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Button size="small" onClick={() => handleMoveUp(index)} disabled={index === 0}>▲</Button>
                    <Button size="small" onClick={() => handleMoveDown(index)}
                            disabled={index === items.length - 1}>▼</Button>
                    <Button size="small" danger onClick={() => handleRemoveItem(index)}>삭제</Button>
                </div>
            ),
        },
    ];

    if (isItemLoading || isEventTableLoading) return <IsLoading/>;

    return (
        <div style={{marginTop: 26}}>
            <Form layout="vertical">
                <div style={{display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 4}}>
                    <Form.Item label="데이터 프리셋 로드" style={{flex: 1, marginBottom: 0}}>
                        <Select
                            value={selectedTblIdx}
                            onChange={setSelectedTblIdx}
                            placeholder="그룹 번호 선택"
                            showSearch
                        >
                            {groupKeys.map(k => (
                                <Option key={k} value={Number(k)}>
                                    그룹 {k} ({groupedTbl[k]?.length || 0}개)
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item style={{marginBottom: 0}}>
                        <Button onClick={handleApplyTbl}>적용</Button>
                    </Form.Item>
                </div>
                {selectedTblIdx != null && groupedTbl[selectedTblIdx] && (
                    <div style={{marginBottom: 12, fontSize: 12, color: '#888'}}>
                        인덱스: {groupedTbl[selectedTblIdx].map(e => e._idx).join(', ')}
                    </div>
                )}

                <div style={{display: 'flex', gap: 12, alignItems: 'flex-end'}}>
                    <Form.Item label="그룹 번호" style={{width: 160, marginBottom: 12}}>
                        <InputNumber
                            min={1}
                            value={groupIdx}
                            onChange={val => setGroupIdx(val || 0)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="Memo (전체 항목 공통)" style={{flex: 1, marginBottom: 12}}>
                        <Input
                            value={memo}
                            onChange={e => setMemo(e.target.value)}
                            placeholder="그룹 메모 입력"
                        />
                    </Form.Item>
                </div>
                <div style={{overflowX: 'auto', marginBottom: 12}}>
                    <Table
                        columns={tableCols}
                        dataSource={items.map((it, i) => ({...it, key: i}))}
                        pagination={false}
                        size="small"
                        scroll={{x: 'max-content'}}
                    />
                </div>

                <Button
                    type="dashed"
                    onClick={handleAddItem}
                    style={{width: '100%', marginBottom: 16}}
                >
                    + 추가하기
                </Button>

                <div style={{position: 'sticky', bottom: 0, paddingTop: 8, background: 'var(--ant-color-bg-elevated, #fff)', zIndex: 10}}>
                    <FixButton
                        onClick={handleFix}
                        handleSetDialog={handleSetDialog}
                        disabled={record?.isFixed}
                    />
                    <SaveButton
                        onClick={handleSave}
                        handleSetDialog={handleSetDialog}
                        disabled={record?.isFixed}
                    />
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton
                        onClick={handleDelete}
                        handleSetDialog={handleSetDialog}
                        display={record?.groupIdx > 0 ? 'flex' : 'none'}
                        disabled={record?.isFixed}
                    />
                </div>
            </Form>
        </div>
    );
};

export default ShoppingRoadEventEditModal;